/**
 * OpenNebula service quotas + resource usage (billing). Quotas live on the
 * s10003 gateway; usage rows are served by the worker path
 * `/workers/services/:id/usages` (NOT s10003).
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';
// Quotas go through the compute (v3) gateway via `serviceApi`/`onPath`. Usage rows
// are a NON-compute worker path (`/workers/...`) the s10003 proxy does not cover —
// hit the raw v2 client directly.
import { serviceApi as serviceApiV2 } from '@/api/client';

/** Quota maximums for the service. */
export interface Quotas {
  instances?: number;
  cores?: number;
  /** GiB */
  memory?: number;
  /** GiB */
  volumes?: number;
  networks?: number;
  snapshots?: number;
  backups?: number;
}

export function useQuotas(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.quotas(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const body = unwrap<{ quotas?: Quotas }>(await serviceApi.get(onPath(serviceId!, 'quotas')));
      return body.quotas ?? {};
    },
  });
}

// ---- Quota viewers (live resource counts) --------------------------------
// `quotas/viewers` returns the current count of each resource type with the time
// it was last computed; DELETE forces a recompute ("refresh").

export interface QuotaViewer {
  name: string;
  key: string;
  value: number;
  icon?: string;
  disabled?: boolean;
}
export interface QuotaViewers {
  refreshed_at?: string;
  viewers: QuotaViewer[];
}

export function useQuotaViewers(serviceId: string | undefined) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'quotas', 'viewers'] as const,
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<QuotaViewers> => {
      const body = unwrap<QuotaViewers>(await serviceApi.get(onPath(serviceId!, 'quotas/viewers')));
      return { refreshed_at: body?.refreshed_at, viewers: body?.viewers ?? [] };
    },
  });
}

export function useRefreshQuotaViewers(serviceId: string, options?: UseMutationOptions<unknown, unknown, void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await serviceApi.delete(onPath(serviceId, 'quotas/viewers'))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: ['on', serviceId, 'quotas', 'viewers'] });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Resource limits + quota validation ----------------------------------
// `/s10003/limits` returns how much of each quota REMAINS (limit − used). The
// create flow compares a requested instance against it to block over-quota
// launches. Ported from @console hooks/instance.ts `useQuotaValidator` (which
// reads `resolveResourceLimitation` = `/limits`).

/** Remaining quota per resource (undefined = no limit on that resource). */
export interface ResourceLimits {
  cores?: number;
  /** GiB */
  memory?: number;
  /** GiB */
  volumes?: number;
  instances?: number;
  networks?: number;
  snapshots?: number;
  backups?: number;
}

export function useResourceLimits(serviceId: string | undefined) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'limits'] as const,
    enabled: !!serviceId,
    retry: false,
    staleTime: 15_000,
    queryFn: async () => {
      const body = unwrap<{ limits?: ResourceLimits }>(
        await serviceApi.get(onPath(serviceId!, 'limits')),
      );
      return body.limits ?? {};
    },
  });
}

/** Parse a flavor code `"<code>-<type>-<cores>-<memoryGiB>"` → cores + memory. */
export function parseFlavorName(flavor?: string): { cores: number; memory: number } {
  const [, , cores, memory] = (flavor ?? '').split('-');
  return { cores: Number(cores) || 0, memory: Number(memory) || 0 };
}

export interface QuotaRequest {
  cores: number;
  /** GiB */
  memory: number;
  /** GiB */
  volumes: number;
  instances: number;
}

export interface QuotaValidation {
  /** True when the request would exceed the remaining quota on any resource. */
  invalid: boolean;
  /** Remaining quota AFTER this request, per resource (negative = exceeds). */
  remaining: QuotaRequest;
  limits: ResourceLimits;
  isLoading: boolean;
}

/**
 * Validate a requested instance against the service's remaining quota. A resource
 * with no limit (absent from `/limits`) never blocks. Returns the post-request
 * headroom per resource so the form can surface "x cores over quota".
 */
export function useQuotaValidator(
  serviceId: string | undefined,
  request: Partial<QuotaRequest>,
): QuotaValidation {
  const { data: limits, isLoading } = useResourceLimits(serviceId);
  const cores = request.cores ?? 0;
  const memory = request.memory ?? 0;
  const volumes = request.volumes ?? 0;
  const instances = request.instances ?? 1;
  return useMemo(() => {
    const lim = limits ?? {};
    const headroom = (limit: number | undefined, req: number) =>
      (limit ?? Number.POSITIVE_INFINITY) - req;
    const remaining: QuotaRequest = {
      cores: headroom(lim.cores, cores),
      memory: headroom(lim.memory, memory),
      volumes: headroom(lim.volumes, volumes),
      instances: headroom(lim.instances, instances),
    };
    const invalid =
      remaining.cores < 0 ||
      remaining.memory < 0 ||
      remaining.volumes < 0 ||
      remaining.instances < 0;
    return { invalid, remaining, limits: lim, isLoading };
  }, [limits, cores, memory, volumes, instances, isLoading]);
}

// ---- Instance launch configuration (flavors, networks, secgroups, images) -
// `configurations/instance` returns flavors + networks + secgroups + an
// `initialize` payload (a valid, pre-filled create body — the source of truth for
// the create payload shape). Images come from a SEPARATE `/images` endpoint.

export interface ConfigImage {
  id: string;
  name?: string;
  distro?: string;
  type?: string;
  version?: string;
  /** Default disk size (GiB). */
  volume?: number;
  template_id?: string | null;
  disk_locked?: boolean;
}

export interface ConfigFlavor {
  id?: string;
  name: string;
  type?: string;
  cores?: number;
  memory?: number;
  available?: number;
  disabled?: boolean;
}

/** A launchable network (NIC target). Mirrors the legacy `Network` shape. */
export interface ConfigNetwork {
  id: string;
  name: string;
  description?: string;
  uid?: string;
  type?: string;
  prefix?: string;
  zone?: string;
  metadata_id?: string;
  network_referrer_type?: string;
  network_referrer_id?: string;
}

/** A rule inside a security group template. */
export interface SecgroupRuleTpl {
  protocol?: string;
  range?: string;
  ref_type?: string;
  rule_type?: 'INBOUND' | 'OUTBOUND' | string;
  ip?: string;
  /** Address count of the range — base `ip` + `size`, not CIDR (see `ruleCidr`). */
  size?: string;
}

export interface ConfigSecgroup {
  id: string;
  uid?: string;
  name?: string;
  attached?: unknown[];
  template?: { name?: string; rule?: SecgroupRuleTpl[] };
}

/** A volume in a create payload. */
export interface InitVolume {
  name: string;
  /** GiB */
  size: number;
  type: string;
}

/** The pre-filled, valid create payload skeleton the backend suggests. */
export interface InitializePayload {
  name?: string;
  image?: string;
  flavor?: string;
  networks?: ConfigNetwork[];
  volumes?: InitVolume[];
}

/** A GPU model offered for GPU-accelerated flavors. */
export interface GpuModel {
  id: string;
  label?: string;
  vram?: number;
  device_id?: string;
}

export interface InstanceConfig {
  flavors: ConfigFlavor[];
  networks: ConfigNetwork[];
  secgroups: ConfigSecgroup[];
  /** Distribution (system) OS images — grouped by `distro` for the OS picker. */
  images: ConfigImage[];
  gpuModels: GpuModel[];
  initialize?: InitializePayload;
}

export function useInstanceConfig(serviceId: string | undefined) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'config', 'instance'] as const,
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<InstanceConfig> => {
      const body = unwrap<Partial<InstanceConfig> & { gpu_models?: GpuModel[] }>(
        await serviceApi.get(onPath(serviceId!, 'configurations/instance')),
      );
      return {
        flavors: body.flavors ?? [],
        networks: body.networks ?? [],
        secgroups: body.secgroups ?? [],
        images: body.images ?? [],
        gpuModels: body.gpu_models ?? [],
        initialize: body.initialize,
      };
    },
  });
}

/** Launch config for a non-instance resource type (e.g. `load-balancer`).
 *  Returns flavors/networks/secgroups + a pre-filled `initialize` payload. */
export function useTypedConfig(serviceId: string | undefined, type: string) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'config', type] as const,
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<Pick<InstanceConfig, 'flavors' | 'networks' | 'secgroups' | 'initialize'>> => {
      const body = unwrap<Partial<InstanceConfig>>(
        await serviceApi.get(onPath(serviceId!, `configurations/${type}`)),
      );
      return {
        flavors: body.flavors ?? [],
        networks: body.networks ?? [],
        secgroups: body.secgroups ?? [],
        initialize: body.initialize,
      };
    },
  });
}

/** Custom/saved images (`/s10003/images`) — the user's own images, separate
 *  from the distribution catalog in `configurations/instance.images`. */
export function useImages(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.images(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const body = unwrap<{ images?: ConfigImage[] } | ConfigImage[]>(
        await serviceApi.get(onPath(serviceId!, 'images')),
      );
      return Array.isArray(body) ? body : body.images ?? [];
    },
  });
}

export interface UsageRow {
  instance_id?: string;
  instance_name?: string;
  instance_state?: string;
  instance_cpu?: number;
  instance_ram?: number;
  instance_volume?: number;
  instance_image?: string;
  /** The worker `/usages` rows expose two IP fields; there is no flat `instance_ip`. */
  instance_assigned_ip?: string;
  instance_floating_ip?: string;
  instance_duration?: number;
  instance_duration_start?: string;
  instance_duration_end?: string;
  instance_created_at?: string;
}

/** Usages query — matches @console: `gte`/`lte` window + `commitments[]`
 *  (`['virtual-machine','load-balancer']`) so LB usage is included. */
export interface UsagesQuery {
  gte?: string;
  lte?: string;
  /** Commitment kinds to include, e.g. `['virtual-machine', 'load-balancer']`. */
  commitments?: string[];
}

export function useResourceUsages(
  serviceId: string | undefined,
  query?: UsagesQuery,
) {
  return useQuery({
    queryKey: onKeys.usages(serviceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const body = unwrap<{ usages?: { values?: UsageRow[] }[] }>(
        await serviceApiV2.get(`/workers/services/${serviceId}/usages`, { params: query }),
      );
      return (body.usages ?? []).flatMap((u) => u.values ?? []);
    },
  });
}
